import React from "react"
import { Bell, CheckCheck, ExternalLink, Loader2 } from "lucide-react"
import { cn } from "src/utils/cn"
import { useNotifications } from "../hooks/useNotifications"
import { useNavigate } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "src/components/ui/dropdown-menu"

const TYPE_LABELS = {
  task_assigned:    "Task assigned",
  task_due:         "Task due",
  task_overdue:     "Task overdue",
  task_completed:   "Task completed",
  lead_assigned:    "New lead",
  lead_sla_breach:  "SLA breach",
  lead_sla_warning: "SLA warning",
  reminder:         "Reminder",
}

const TYPE_COLORS = {
  task_assigned:   "bg-blue-100 text-blue-700",
  task_due:        "bg-amber-100 text-amber-700",
  task_overdue:    "bg-red-100 text-red-700",
  lead_assigned:   "bg-emerald-100 text-emerald-700",
  lead_sla_breach: "bg-red-100 text-red-700",
  reminder:        "bg-violet-100 text-violet-700",
}

function timeAgo(dateStr) {
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (secs < 60) return "just now"
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return `${Math.floor(secs / 86400)}d ago`
}

function notifiableHref(n) {
  if (!n.notifiable) return null
  const { type, id } = n.notifiable
  if (type === "Task")    return `/my-day`
  if (type === "Lead")    return `/leads/${id}`
  if (type === "Deal")    return `/deals/${id}`
  if (type === "Booking") return `/bookings/${id}`
  return null
}

export function NotificationBell() {
  const { notifications, unreadCount, loading, markRead, markAll } = useNotifications()
  const navigate = useNavigate()

  function handleClick(n) {
    if (!n.read) markRead(n.id)
    const href = notifiableHref(n)
    if (href) navigate(href)
  }

  return (
    <DropdownMenu>
      {/* Trigger — bell with badge at top-right */}
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none ring-2 ring-white pointer-events-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      {/* Panel — portalled outside sidebar so overflow never clips it */}
      <DropdownMenuContent
        side="right"
        align="end"
        sideOffset={12}
        className="w-80 p-0 overflow-hidden rounded-xl shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-sm font-semibold text-slate-800">Notifications</span>
          {unreadCount > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); markAll() }}
              className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700"
            >
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>

        {/* List */}
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-50">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell className="mx-auto mb-2 h-7 w-7 text-slate-200" />
              <p className="text-sm text-slate-500">No notifications yet</p>
              <p className="mt-0.5 text-xs text-slate-400">You're all caught up!</p>
            </div>
          ) : (
            notifications.map((n) => {
              const href = notifiableHref(n)
              return (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={cn(
                    "group flex cursor-pointer gap-3 px-4 py-3 transition-colors hover:bg-slate-50",
                    !n.read && "bg-blue-50/50"
                  )}
                >
                  {/* Unread dot */}
                  <div className="mt-1.5 flex-shrink-0">
                    <div className={cn("h-2 w-2 rounded-full", n.read ? "bg-transparent" : "bg-blue-500")} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn("text-[13px] leading-snug text-slate-800", !n.read && "font-medium")}>
                        {n.title}
                      </p>
                      {href && (
                        <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0 text-slate-300 group-hover:text-slate-500" />
                      )}
                    </div>
                    {n.body && (
                      <p className="mt-0.5 text-[12px] text-slate-500 line-clamp-2">{n.body}</p>
                    )}
                    <div className="mt-1.5 flex items-center gap-2">
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[10px] font-medium",
                          TYPE_COLORS[n.notification_type] || "bg-slate-100 text-slate-600"
                        )}
                      >
                        {TYPE_LABELS[n.notification_type] || n.notification_type}
                      </span>
                      <span className="text-[11px] text-slate-400">{timeAgo(n.created_at)}</span>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
